import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Users, Plus, Edit, Trash2 } from 'lucide-react';

const Funcionarios = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFuncionario, setEditingFuncionario] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [formData, setFormData] = useState({
    funcionario_nome: '',
    funcionario_cpf: '',
    funcionario_rg: '',
    funcionario_telefone: '',
    funcionario_email: '',
    funcionario_endereco: '',
    funcionario_cidade: '',
    funcionario_estado: '',
    funcionario_cep: '',
    funcionario_cargo: '',
    funcionario_setor: '',
    funcionario_data_admissao: '',
    funcionario_salario: '',
    funcionario_carga_horaria: '',
    funcionario_turno: '',
    funcionario_horario_inicio: '',
    funcionario_horario_fim: '',
    funcionario_dias_trabalho: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
    funcionario_contrato_tipo: 'clt',
    funcionario_empresa_terceirizada: '',
    funcionario_observacoes: '',
    funcionario_ativo: true
  });

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadFuncionarios();
      loadCargos();
    }
  }, [condominioSelecionado]);

  const loadFuncionarios = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) {
        setFuncionarios([]);
        return;
      }
      
      const response = await api.get(`/funcionarios?condominio_id=${condominioId}`);
      setFuncionarios(response.data.data.funcionarios || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCargos = async () => {
    try {
      const response = await api.get('/funcionarios/cargos/disponiveis');
      setCargos(response.data.data.cargos || []);
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      if (editingFuncionario) {
        await api.put(`/funcionarios/${editingFuncionario.funcionario_id}`, formData);
      } else {
        await api.post('/funcionarios', formData);
      }
      setShowModal(false);
      setEditingFuncionario(null);
      resetForm();
      loadFuncionarios();
    } catch (error) {
      console.error('Erro ao salvar funcionário:', error);
      
      // Tratar diferentes tipos de erro
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Se houver múltiplos erros de validação
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (funcionario) => {
    setEditingFuncionario(funcionario);
    setFormData({
      funcionario_nome: funcionario.funcionario_nome || '',
      funcionario_cpf: funcionario.funcionario_cpf || '',
      funcionario_rg: funcionario.funcionario_rg || '',
      funcionario_telefone: funcionario.funcionario_telefone || '',
      funcionario_email: funcionario.funcionario_email || '',
      funcionario_endereco: funcionario.funcionario_endereco || '',
      funcionario_cidade: funcionario.funcionario_cidade || '',
      funcionario_estado: funcionario.funcionario_estado || '',
      funcionario_cep: funcionario.funcionario_cep || '',
      funcionario_cargo: funcionario.funcionario_cargo || '',
      funcionario_setor: funcionario.funcionario_setor || '',
      funcionario_data_admissao: funcionario.funcionario_data_admissao ? funcionario.funcionario_data_admissao.split('T')[0] : '',
      funcionario_salario: funcionario.funcionario_salario || '',
      funcionario_carga_horaria: funcionario.funcionario_carga_horaria || '',
      funcionario_turno: funcionario.funcionario_turno || '',
      funcionario_horario_inicio: funcionario.funcionario_horario_inicio || '',
      funcionario_horario_fim: funcionario.funcionario_horario_fim || '',
      funcionario_dias_trabalho: funcionario.funcionario_dias_trabalho || ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
      funcionario_contrato_tipo: funcionario.funcionario_contrato_tipo || 'clt',
      funcionario_empresa_terceirizada: funcionario.funcionario_empresa_terceirizada || '',
      funcionario_observacoes: funcionario.funcionario_observacoes || '',
      funcionario_ativo: funcionario.funcionario_ativo !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este funcionário?')) {
      try {
        await api.delete(`/funcionarios/${id}`);
        loadFuncionarios();
      } catch (error) {
        console.error('Erro ao excluir funcionário:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      funcionario_nome: '',
      funcionario_cpf: '',
      funcionario_rg: '',
      funcionario_telefone: '',
      funcionario_email: '',
      funcionario_endereco: '',
      funcionario_cidade: '',
      funcionario_estado: '',
      funcionario_cep: '',
      funcionario_cargo: '',
      funcionario_setor: '',
      funcionario_data_admissao: '',
      funcionario_salario: '',
      funcionario_carga_horaria: '',
      funcionario_turno: '',
      funcionario_horario_inicio: '',
      funcionario_horario_fim: '',
      funcionario_dias_trabalho: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
      funcionario_contrato_tipo: 'clt',
      funcionario_empresa_terceirizada: '',
      funcionario_observacoes: '',
      funcionario_ativo: true
    });
    setErrorMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Aplicar máscara para telefone
    if (name === 'funcionario_telefone') {
      const phoneValue = value.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
      let formattedPhone = '';
      
      if (phoneValue.length <= 2) {
        formattedPhone = phoneValue;
      } else if (phoneValue.length <= 6) {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2)}`;
      } else if (phoneValue.length <= 10) {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2, 6)}-${phoneValue.slice(6)}`;
      } else {
        formattedPhone = `(${phoneValue.slice(0, 2)}) ${phoneValue.slice(2, 7)}-${phoneValue.slice(7, 11)}`;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
      return;
    }
    
    // Aplicar máscara para CPF
    if (name === 'funcionario_cpf') {
      const cpfValue = value.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
      let formattedCpf = '';
      
      if (cpfValue.length <= 3) {
        formattedCpf = cpfValue;
      } else if (cpfValue.length <= 6) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3)}`;
      } else if (cpfValue.length <= 9) {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3, 6)}.${cpfValue.slice(6)}`;
      } else {
        formattedCpf = `${cpfValue.slice(0, 3)}.${cpfValue.slice(3, 6)}.${cpfValue.slice(6, 9)}-${cpfValue.slice(9, 11)}`;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedCpf
      }));
      return;
    }
    
    // Aplicar máscara para CEP
    if (name === 'funcionario_cep') {
      const cepValue = value.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
      let formattedCep = '';
      
      if (cepValue.length <= 5) {
        formattedCep = cepValue;
      } else {
        formattedCep = `${cepValue.slice(0, 5)}-${cepValue.slice(5, 8)}`;
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedCep
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getCargoLabel = (cargo) => {
    const cargoObj = cargos.find(c => c.value === cargo);
    return cargoObj ? cargoObj.label : cargo;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="page-container">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Funcionários</h1>
        {['sindico', 'subsindico', 'administrador'].includes(user?.tipo) && (
          <button
            onClick={() => {
              setEditingFuncionario(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Novo Funcionário
          </button>
        )}
      </div>

      {/* Lista de Funcionários */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-container">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPF
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cargo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admissão
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {funcionarios.map((funcionario) => (
                <tr key={funcionario.funcionario_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {funcionario.funcionario_nome}
                    </div>
                    {funcionario.funcionario_email && (
                      <div className="text-sm text-gray-500">
                        {funcionario.funcionario_email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {funcionario.funcionario_cpf}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {getCargoLabel(funcionario.funcionario_cargo)}
                    </span>
                    {funcionario.funcionario_setor && (
                      <div className="text-xs text-gray-500 mt-1">
                        {funcionario.funcionario_setor}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {funcionario.funcionario_telefone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(funcionario.funcionario_data_admissao)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {funcionario.funcionario_salario ? formatCurrency(funcionario.funcionario_salario) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      funcionario.funcionario_ativo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {funcionario.funcionario_ativo ? 'Ativo' : 'Inativo'}
                    </span>
                    {funcionario.funcionario_data_demissao && (
                      <div className="text-xs text-red-600 mt-1">
                        Demitido em {formatDate(funcionario.funcionario_data_demissao)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(funcionario)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {['sindico', 'administrador'].includes(user?.tipo) && (
                        <button
                          onClick={() => handleDelete(funcionario.funcionario_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingFuncionario ? 'Editar Funcionário' : 'Novo Funcionário'}
              </h3>
              
              {/* Exibir mensagem de erro */}
              {errorMessage && (
                <div className="mb-4 alert-error border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Erro ao salvar funcionário
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 grid-cols-2 gap-4">
                  {/* Dados Pessoais */}
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Dados Pessoais</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nome *</label>
                    <input
                      type="text"
                      name="funcionario_nome"
                      value={formData.funcionario_nome}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CPF *</label>
                    <input
                      type="text"
                      name="funcionario_cpf"
                      value={formData.funcionario_cpf}
                      onChange={handleInputChange}
                      required
                      placeholder="000.000.000-00"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">RG</label>
                    <input
                      type="text"
                      name="funcionario_rg"
                      value={formData.funcionario_rg}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Telefone *</label>
                    <input
                      type="text"
                      name="funcionario_telefone"
                      value={formData.funcionario_telefone}
                      onChange={handleInputChange}
                      required
                      placeholder="(11) 99999-9999"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="funcionario_email"
                      value={formData.funcionario_email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Endereço */}
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Endereço</h4>
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Endereço</label>
                    <input
                      type="text"
                      name="funcionario_endereco"
                      value={formData.funcionario_endereco}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cidade</label>
                    <input
                      type="text"
                      name="funcionario_cidade"
                      value={formData.funcionario_cidade}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Estado</label>
                    <input
                      type="text"
                      name="funcionario_estado"
                      value={formData.funcionario_estado}
                      onChange={handleInputChange}
                      maxLength="2"
                      placeholder="SP"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CEP</label>
                    <input
                      type="text"
                      name="funcionario_cep"
                      value={formData.funcionario_cep}
                      onChange={handleInputChange}
                      placeholder="00000-000"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Dados Profissionais */}
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Dados Profissionais</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cargo *</label>
                    <select
                      name="funcionario_cargo"
                      value={formData.funcionario_cargo}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecione o cargo</option>
                      {cargos.map(cargo => (
                        <option key={cargo.value} value={cargo.value}>
                          {cargo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Setor</label>
                    <input
                      type="text"
                      name="funcionario_setor"
                      value={formData.funcionario_setor}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data de Admissão *</label>
                    <input
                      type="date"
                      name="funcionario_data_admissao"
                      value={formData.funcionario_data_admissao}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Salário (R$)</label>
                    <input
                      type="number"
                      name="funcionario_salario"
                      value={formData.funcionario_salario}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Carga Horária (h/semana)</label>
                    <input
                      type="number"
                      name="funcionario_carga_horaria"
                      value={formData.funcionario_carga_horaria}
                      onChange={handleInputChange}
                      min="1"
                      max="44"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Turno</label>
                    <select
                      name="funcionario_turno"
                      value={formData.funcionario_turno}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecione o turno</option>
                      <option value="manha">Manhã</option>
                      <option value="tarde">Tarde</option>
                      <option value="noite">Noite</option>
                      <option value="integral">Integral</option>
                      <option value="escala">Escala</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Horário de Início</label>
                    <input
                      type="time"
                      name="funcionario_horario_inicio"
                      value={formData.funcionario_horario_inicio}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Horário de Fim</label>
                    <input
                      type="time"
                      name="funcionario_horario_fim"
                      value={formData.funcionario_horario_fim}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tipo de Contrato</label>
                    <select
                      name="funcionario_contrato_tipo"
                      value={formData.funcionario_contrato_tipo}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="clt">CLT</option>
                      <option value="pj">PJ</option>
                      <option value="terceirizado">Terceirizado</option>
                      <option value="estagiario">Estagiário</option>
                      <option value="temporario">Temporário</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Empresa Terceirizada</label>
                    <input
                      type="text"
                      name="funcionario_empresa_terceirizada"
                      value={formData.funcionario_empresa_terceirizada}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                    <textarea
                      name="funcionario_observacoes"
                      value={formData.funcionario_observacoes}
                      onChange={handleInputChange}
                      rows="3"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="funcionario_ativo"
                        checked={formData.funcionario_ativo}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700">Funcionário Ativo</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingFuncionario(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-md text-white ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Salvando...' : (editingFuncionario ? 'Atualizar' : 'Criar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;
